import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    ProfileSettingsContainer,
    FormField,
    ProfilePicturePreview,
    ProfilePictureContainer,
    StyledFileInputLabel,
    HiddenFileInput,
    HeaderContainer,
    BackButton
} from '../../styles/ProfileSettings.styles';
import ChevronIcon from '../icons/ChevronIcon';

const ProfileSettings = ({ onBack }) => {
    const { user, updateUserProfile } = useAuth();
    const [displayName, setDisplayName] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(''); // For error messages
    const [updateStatus, setUpdateStatus] = useState('idle'); // 'idle', 'success', 'error'

    useEffect(() => {
        if (user) {
            setDisplayName(user.displayName || '');
            setPhotoPreview(user.photoURL || null);
            setUpdateStatus('idle'); // Reset status when user context changes
        }
    }, [user]);

    const handleDisplayNameChange = (e) => {
        setDisplayName(e.target.value);
        if (updateStatus === 'success') {
            setUpdateStatus('idle'); // Reset button text if user types after success
        }
        setMessage(''); // Clear any previous error messages
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
            if (updateStatus === 'success') {
                setUpdateStatus('idle'); // Reset button text if user changes file after success
            }
            setMessage(''); // Clear any previous error messages
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;
        setUpdateStatus('idle'); // Reset status before new submission attempt
        setMessage('');


        const trimmedDisplayName = displayName.trim();
        const noDisplayNameChange = trimmedDisplayName === (user.displayName || ''); // Handle if initial displayName is null/undefined
        const noPhotoChange = !photoFile;

        if (noDisplayNameChange && noPhotoChange) {
            setMessage('No changes to submit.'); // Or setUpdateStatus('idle') and do nothing
            return;
        }

        // Allow updating photo even if display name is empty, but not an empty display name if it was previously filled
        if (trimmedDisplayName === '' && user.displayName !== '' && !photoFile) {
             setMessage('Display name cannot be empty if it was previously set and you are not changing the photo.');
             return;
        }


        setLoading(true);

        try {
            await updateUserProfile({ displayName: trimmedDisplayName, photoFile });
            setUpdateStatus('success');
            // setMessage(''); // Success is handled by button text
            if (!photoFile && user.photoURL !== photoPreview) {
                // If only display name was updated, ensure preview reflects current user.photoURL
                // This handles cases where user.photoURL might be null initially
                setPhotoPreview(user.photoURL || null);
            }
            setPhotoFile(null);
        } catch (error) {
            console.error("Error updating profile:", error);
            setMessage(`Error updating profile: ${error.message}`);
            setUpdateStatus('error');
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return <p>Loading user profile...</p>;
    }

    const isButtonDisabled = loading ||
                           (!displayName.trim() && !photoFile && !user.displayName) ||
                           (displayName.trim() === (user.displayName || '') && !photoFile && updateStatus !== 'success');


    let buttonText = 'Update Profile';
    if (loading) {
        buttonText = 'Updating...';
    } else if (updateStatus === 'success') {
        buttonText = 'Updated Successfully!';
    }

    return (
        <ProfileSettingsContainer>
            <HeaderContainer>
                {onBack && (
                    <BackButton onClick={onBack} title="Back to Settings">
                        <ChevronIcon direction="left" />
                    </BackButton>
                )}
                <h3>Edit Profile</h3>
            </HeaderContainer>
            {message && updateStatus === 'error' && <p className="error-message">{message}</p>}
            {/* Success message is now part of the button text */}
            <form onSubmit={handleSubmit}>
                <FormField>
                    <label htmlFor="displayName">Display Name:</label>
                    <input
                        type="text"
                        id="displayName"
                        value={displayName}
                        onChange={handleDisplayNameChange}
                    />
                </FormField>
                <FormField>
                    <label htmlFor="photo">Profile Picture:</label>
                    <ProfilePictureContainer>
                        {photoPreview && <ProfilePicturePreview src={photoPreview} alt="Profile Preview" />}
                    </ProfilePictureContainer>
                    <HiddenFileInput
                        type="file"
                        id="photo"
                        accept="image/*"
                        onChange={handleFileChange}
                    />
                    <StyledFileInputLabel htmlFor="photo">
                        {photoFile ? photoFile.name : "Choose a file"}
                    </StyledFileInputLabel>
                </FormField>
                <button type="submit" disabled={isButtonDisabled}>
                    {buttonText}
                </button>
            </form>
        </ProfileSettingsContainer>
    );
};

export default ProfileSettings;
